import React from 'react';
import { Link } from 'react-router-dom';
import { formatClockTime } from '../utils/dateFormatter';
import { formatHoursMinutesAr } from '../utils/numberFormatter';
import { WINDOW_STATUS } from '../utils/turnoverCodes';

// The cleaning window under one time picker of the operational-settings card
// (tasks/arrival-readiness-action-side.md Phase 2, L5 + the approved Arabic copy).
//
// Presentation only: every number here comes from the server's preview, which is the same function
// the save enforces with. Wording says "cleaning window" and never implies the cleaner is booked for
// that time (L13) — the cleaning task still follows the unit's default checkout until Phase 6.

// A previous departure a day or more earlier is not a same-day turnover — say so rather than
// printing "٧٦ ساعة".
const SAME_DAY_LIMIT_MINUTES = 24 * 60;

const OBSERVED_BY = {
  manual: 'أكد الفريق خروج الضيف',
  guest: 'أبلغ الضيف بمغادرته'
};

function windowLine(side, data, blocking, relocated) {
  const isArrival = side === 'arrival';
  const noNeighbour = isArrival ? 'لا يوجد ضيف مغادر في نفس اليوم' : 'لا يوجد ضيف قادم في نفس اليوم';

  if (!data || data.status === WINDOW_STATUS.NOT_APPLICABLE) return { tone: 'quiet', text: noNeighbour };
  // A relocated guest's unit cannot be proven, so the server never blocks it — and the card must
  // not claim a window it cannot stand behind either.
  if (data.status === WINDOW_STATUS.UNKNOWN || relocated) {
    return { tone: 'quiet', text: 'تعذّر حساب نافذة التنظيف — تحقق من أوقات الوحدة' };
  }

  if (data.status === WINDOW_STATUS.NEGATIVE) {
    const clash = isArrival
      ? `الضيف السابق يغادر ${formatClockTime(data.departureAt)}، بعد وقت الوصول المختار`
      : `الضيف التالي يصل ${formatClockTime(data.arrivalAt)}، قبل وقت المغادرة المختار`;
    return { tone: 'block', text: blocking ? `لا يمكن الحفظ: ${clash}` : clash };
  }

  if (data.minutes >= SAME_DAY_LIMIT_MINUTES) return { tone: 'quiet', text: noNeighbour };

  if (data.status === WINDOW_STATUS.TIGHT) {
    return {
      tone: 'tight',
      text: data.minutes === 0
        ? 'نافذة التنظيف: لا يوجد وقت — الضيف يصل لحظة مغادرة الضيف السابق'
        : `نافذة التنظيف: ${formatHoursMinutesAr(data.minutes)} — قد لا يكتمل التنظيف قبل وصول الضيف`
    };
  }
  return { tone: 'ok', text: `نافذة التنظيف: ${formatHoursMinutesAr(data.minutes)}` };
}

/**
 * @param {'arrival'|'departure'} side
 * @param {Object|null} data      - `turnover.arrival` or `turnover.departure` from the server
 * @param {boolean} blocking      - this side is the one refusing the save
 * @param {boolean} relocated
 * @param {boolean} loading
 */
export default function TurnoverWindowNote({ side, data, blocking, relocated, loading }) {
  if (loading && !data) return <p className="turnover-note turnover-note--quiet">جارٍ حساب نافذة التنظيف…</p>;

  const line = windowLine(side, data, blocking, relocated);
  const isArrival = side === 'arrival';
  // Only beside an actual same-day window (ok / tight / block). A checkout recorded days earlier
  // otherwise printed a bare clock time — «غادر الضيف السابق ١٢:٣٧ م» — under «لا يوجد ضيف مغادر في
  // نفس اليوم», reading as "left at 12:37 today" (prod, HMPA9Y4JF9, 2026-09-22). Quiet lines (no
  // neighbour, cannot compute, relocated) make no claim about a departure, so neither does this.
  const observedSource = isArrival && line.tone !== 'quiet' ? data?.departureObservedSource : null;
  // On the arrival side the request is THIS guest's; on the departure side it would be the next
  // guest's, which is not what this picker is about.
  const requested = isArrival ? data?.requestedArrivalAt : null;
  const offerCheckoutShortcut = isArrival
    && data?.status === WINDOW_STATUS.NEGATIVE
    && !relocated
    && data?.neighbour?._id;

  return (
    <div className="turnover-note-group" aria-live="polite">
      <p className={`turnover-note turnover-note--${line.tone}`}>{line.text}</p>
      {offerCheckoutShortcut && (
        <Link className="turnover-note-action" to={`/bookings/${data.neighbour._id}?focus=checkout`}>
          الضيف السابق غادر؟ سجّل وقت مغادرته
        </Link>
      )}
      {observedSource && OBSERVED_BY[observedSource] && (
        <p className="turnover-note turnover-note--info">
          غادر الضيف السابق {formatClockTime(data.departureAt)} — {OBSERVED_BY[observedSource]}
        </p>
      )}
      {requested && (
        <p className="turnover-note turnover-note--info">
          الضيف طلب الوصول {formatClockTime(requested)} — غير معتمد
        </p>
      )}
    </div>
  );
}
